import Header from '../Components/Header';
import Footer from '../Components/Footer';
import { Helmet } from 'react-helmet';
import { SITE_ORIGIN, canonicalUrl } from '../siteConfig';
import { SERVICE_AREAS, SCHEMA_AREA_SERVED } from '../serviceAreas';

const LINEUP_UPDATED = 'July 2026';
const PAGE_URL = canonicalUrl('/products/unifi-networking');

const SCHEMA_SUB_SERVICES = [
  {
    name: 'UniFi Dream Machine Installation',
    description: 'All-in-one gateway, switch, and controller deployment for routing, security, and UniFi OS management.',
  },
  {
    name: 'UniFi UXG Pro Gateway Installation',
    description: 'Dedicated security gateway setup with dual WAN failover and separate UniFi Network controller infrastructure.',
  },
  {
    name: 'UniFi U6 Enterprise and U7 Pro Access Point Installation',
    description: 'Wi-Fi 6, 6E, and Wi-Fi 7 access point placement, tuning, and validation for high-density wireless coverage.',
  },
  {
    name: 'UniFi Managed Switch Installation',
    description: 'PoE and PoE+ switch deployment with VLAN segmentation, port labeling, and rack documentation.',
  },
];

function buildStructuredData(faqs) {
  const townAreas = SERVICE_AREAS.map((name) => ({ '@type': 'City', name }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${SITE_ORIGIN}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Products',
            item: PAGE_URL,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'UniFi Network Installation',
            item: PAGE_URL,
          },
        ],
      },
      {
        '@type': 'Service',
        '@id': `${PAGE_URL}#unifi-network-installation`,
        name: 'UniFi Network Installation',
        description:
          'Ubiquiti UniFi gateway, switch, and wireless installation for homes and offices in Westchester and Fairfield County, including design, deployment, VLAN segmentation, and network documentation.',
        url: PAGE_URL,
        dateModified: '2026-07-04',
        provider: {
          '@type': 'LocalBusiness',
          '@id': `${SITE_ORIGIN}/#localbusiness`,
          name: 'Solace Integrations',
          url: SITE_ORIGIN,
          telephone: '+19145641865',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Hawthorne',
            addressRegion: 'NY',
            addressCountry: 'US',
          },
          areaServed: SCHEMA_AREA_SERVED,
        },
        areaServed: [
          { '@type': 'AdministrativeArea', name: 'Westchester County, NY' },
          { '@type': 'AdministrativeArea', name: 'Fairfield County, CT' },
          ...townAreas,
        ],
        hasPart: SCHEMA_SUB_SERVICES.map((service) => ({
          '@type': 'Service',
          name: service.name,
          description: service.description,
          provider: { '@id': `${SITE_ORIGIN}/#localbusiness` },
          areaServed: townAreas,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${PAGE_URL}#faq`,
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };
}

const KEY_BENEFITS = [
  {
    title: 'One console, whole stack',
    text: 'Gateways, switches, wireless, and optional Protect or Access apps all report into UniFi OS, giving you fewer logins and a single place to see what is online.',
  },
  {
    title: 'No per-device licensing',
    text: 'Unlike subscription-locked platforms that charge per access point or switch, UniFi hardware runs without recurring license fees as you add capacity.',
  },
  {
    title: 'Built to grow with you',
    text: 'Zero-touch AP adoption, PoE switching, and VLAN segmentation let you expand office, guest, and IoT networks without rebuilding from scratch.',
  },
];

const PRODUCTS = [
  {
    name: 'UniFi Dream Machine Pro / SE',
    summary:
      'All-in-one gateway, switch, and network controller for homes and offices that want routing, security, and management in a single rack-mount unit.',
    features: [
      'Integrated routing, firewall, and threat management',
      'Hosts UniFi Network, Protect, Access, and Talk applications',
      'Dual WAN support with failover for ISP redundancy',
    ],
    uiHref: 'https://ui.com/cloud-gateways',
  },
  {
    name: 'UniFi UXG Pro Gateway',
    summary:
      'Standalone security gateway for larger sites that need dedicated throughput and a separate UniFi Network application server.',
    features: [
      'Layer 3 routing with IPS/IDS at sustained gigabit speeds',
      'Policy-based dual WAN with failover and load balancing',
      'Pairs with cloud or on-prem UniFi Network controllers',
    ],
    uiHref: 'https://ui.com/cloud-gateways/uxg-pro',
  },
  {
    name: 'UniFi U6 Enterprise / U7 Pro APs',
    summary:
      'Wi-Fi 6, 6E, and Wi-Fi 7-ready access points for offices, hospitality, and multi-floor properties that need dense client capacity.',
    features: [
      'Tri-band radios with 6 GHz support on U6 Enterprise models',
      'Centralized adoption and RF management through UniFi Network',
      'PoE+ powered with ceiling or wall mounting options',
    ],
    uiHref: 'https://ui.com/wi-fi',
  },
  {
    name: 'UniFi Pro Max & Enterprise Switches',
    summary:
      'Managed PoE and PoE+ switches that power access points, cameras, and phones while keeping rack cabling clean and documented.',
    features: [
      'PoE+ and high-wattage ports sized to your device count',
      'VLAN-aware switching aligned to your segmentation plan',
      'Stacking and uplink options for multi-closet deployments',
    ],
    uiHref: 'https://ui.com/switching',
  },
  {
    name: 'UniFi Protect & Access',
    summary:
      'Optional surveillance and door-access applications that run on the same UniFi OS console as your network, with no separate vendor stack required.',
    features: [
      'Protect: PoE cameras with local recording on console storage',
      'Access: modern door readers managed alongside network gear',
      'Smart detections and event feeds without monthly platform fees',
    ],
    uiHref: 'https://ui.com/camera-security',
  },
];

const WHY_UNIFI = [
  'Enterprise-style VLANs, guest networks, and firewall policies without the recurring licensing model of cloud-only competitors.',
  'Quality of Service rules that prioritize VoIP and video conferencing when Zoom, Teams, or desk phones share the same LAN.',
  'Coverage planning tied to your floor plan, not generic AP counts, with validation after hardware is live.',
  'Labeled racks, patch panels, and port maps so the next technician knows exactly what is where.',
  'Local or cloud-hosted UniFi Network management, depending on whether you want on-site control or remote visibility.',
];

const INSTALL_SERVICES = [
  'Site assessment and wireless modeling to determine AP count, switch sizing, and PoE budget.',
  'Gateway and controller setup including Dream Machine, UXG Pro, or existing hardware you plan to keep.',
  'Managed switch deployment with VLANs, inter-VLAN routing, and guest or captive portal policies.',
  'Access point mounting, channel planning, band steering, and minimum RSSI tuning.',
  'Optional UniFi Protect camera and UniFi Access reader integration on the same UniFi OS console.',
  'Handoff documentation: rack elevations, SSID maps, labeled ports, and configuration exports.',
];

const FAQS = [
  {
    question: 'Is UniFi a good fit for business networks?',
    answer:
      'For small and mid-sized offices, UniFi delivers reliable Wi-Fi, VLAN segmentation, guest access, and centralized management at a lower total cost than many enterprise platforms, largely because there are no per-device license fees stacking up as you grow.',
  },
  {
    question: 'Can UniFi handle VoIP and video conferencing?',
    answer:
      'Yes. QoS policies can prioritize voice and video traffic over general data, which helps prevent dropped calls or frozen meetings when the network is busy. Many clients choose UniFi specifically to stabilize Teams, Zoom, and desk phone traffic.',
  },
  {
    question: 'What is UniFi Protect, and how does it tie into the network?',
    answer:
      'UniFi Protect is Ubiquiti\'s surveillance application. Cameras connect via PoE to your UniFi switches, record to local storage on a UniFi console, and support smart detections without a separate monthly camera platform subscription.',
  },
  {
    question: 'Do you need cloud management, or can UniFi run locally?',
    answer:
      'Both work. UniFi Network can be managed through Ubiquiti\'s cloud portal, a locally installed Network Server on Windows, macOS, or Linux, or a UniFi OS console such as a Dream Machine. We configure whichever model fits your security and remote-access requirements.',
  },
  {
    question: 'How long does a typical UniFi install take?',
    answer:
      'A small office with under ten drops is often a day or two. Mid-size projects with ten to fifty drops usually run several days. Larger builds with extensive cabling, multiple closets, or fiber backbones can take weeks. We scope timeline during design based on your floor plan and pathway availability.',
  },
  {
    question: 'Can you migrate an existing UniFi network?',
    answer:
      'We review your current controller settings, move configurations to new hardware or applications, and clean up SSIDs, VLANs, and user accounts as part of the transition.',
  },
  {
    question: 'Will cables and switch ports be labeled?',
    answer:
      'Yes. Every drop, patch panel, and switch port is labeled and captured in the as-built documentation delivered at project close.',
  },
];

function FaqItem({ question, answer }) {
  return (
    <article className="unifi-page-faq-item">
      <h3 className="unifi-page-faq-question">{question}</h3>
      <p className="unifi-page-faq-answer">{answer}</p>
    </article>
  );
}

function ProductCard({ name, summary, features, uiHref }) {
  return (
    <article className="unifi-page-product-card">
      <h3 className="unifi-page-product-name">{name}</h3>
      <p className="unifi-page-product-summary">{summary}</p>
      <ul className="unifi-page-product-features">
        {features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      <a
        href={uiHref}
        target="_blank"
        rel="noopener noreferrer"
        className="unifi-page-product-link"
      >
        View on UI.com
      </a>
    </article>
  );
}

export default function UniFiNetworking() {
  const structuredData = buildStructuredData(FAQS);

  return (
    <div>
      <Helmet>
        <title>UniFi Network Installation in Westchester &amp; Fairfield County | Solace Integrations</title>
        <link rel="canonical" href={PAGE_URL} />
        <meta
          name="description"
          content="UniFi network installation in Westchester and Fairfield County. Solace Integrations deploys Dream Machine gateways, UXG Pro, Wi-Fi 6/7 access points, and PoE switches with VLAN design, documentation, and ongoing support."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={PAGE_URL} />
        <meta
          property="og:title"
          content="UniFi Network Installation in Westchester &amp; Fairfield County | Solace Integrations"
        />
        <meta
          property="og:description"
          content="Ubiquiti UniFi installation for homes and offices in Westchester and Fairfield County. Gateways, switches, wireless, and optional Protect and Access on one UniFi OS console."
        />
        <meta property="og:image" content={`${SITE_ORIGIN}/assets/images/unifi-gear.jpg`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <Header isFixed />
      <section className="services">
        <div className="services-hero py-5">
          <div className="container py-md-5 py-2">
            <nav className="net-infra-breadcrumb" aria-label="Breadcrumb">
              <a href="/products/unifi-networking">Products</a>
              <span className="net-infra-breadcrumb-sep" aria-hidden="true">/</span>
              <span>UniFi Networking</span>
            </nav>
            <div className="text-center">
              <p className="unifi-page-hero-label">Product Guide</p>
              <h1 className="services-title text-center mb-lg-4 mb-3 px-lg-5">
                UniFi Network Installation in Westchester &amp; Fairfield County
              </h1>
              <p className="main_p4 mt-4 mb-0 pt-2 text-center mx-auto text-white">
                Enterprise-grade routing, switching, and wireless with centralized UniFi OS management,
                specified and deployed for homes and small businesses at a cost structure that scales
                without per-device licensing.
              </p>
              <div className="unifi-page-hero-actions">
                <a href="/contact" className="btn btn-style solace-btn-primary">
                  Plan Your UniFi Project
                </a>
                <a href="/contact" className="btn btn-style unifi-page-hero-secondary">
                  Talk With Us
                </a>
              </div>
              <p className="unifi-page-updated">Product lineup reviewed {LINEUP_UPDATED}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="overview" className="unifi-page-section">
        <div className="container">
          <div className="unifi-page-overview-grid">
            <div className="unifi-page-overview-main">
              <h6 className="solace-title-subhny mb-2">Overview</h6>
              <h2 className="title-w3l text-left">
                Serious networking without the enterprise price tag
              </h2>
              <p className="unifi-page-body">
                Solace Integrations designs and deploys UniFi hardware throughout Westchester County,
                NY and Fairfield County, CT. The platform brings centralized management, clean rack
                hardware, and Wi-Fi 6/7 performance to properties that need more than a consumer router
                but do not want a subscription bill every time an access point goes in.
              </p>
              <p className="unifi-page-body">
                We map coverage to your actual floor plan, size PoE switches for the devices on your
                network, and configure VLANs before users move in. When you are retaining existing
                firewalls or switches, we build around them and upgrade only where the design calls for
                it. Every project ends with labeled cabling and documentation your team can reference
                long after install day.
              </p>
              <div className="unifi-page-benefits">
                {KEY_BENEFITS.map(({ title, text }) => (
                  <article key={title} className="unifi-page-benefit-card">
                    <h3 className="unifi-page-benefit-title">{title}</h3>
                    <p className="unifi-page-benefit-text">{text}</p>
                  </article>
                ))}
              </div>
            </div>
            <aside className="unifi-page-snapshot">
              <h3 className="unifi-page-snapshot-title">At a Glance</h3>
              <dl className="unifi-page-snapshot-list">
                <div>
                  <dt>Platform</dt>
                  <dd>UniFi by Ubiquiti</dd>
                </div>
                <div>
                  <dt>Coverage area</dt>
                  <dd>Westchester &amp; Fairfield County</dd>
                </div>
                <div>
                  <dt>Guide updated</dt>
                  <dd>{LINEUP_UPDATED}</dd>
                </div>
                <div>
                  <dt>Management</dt>
                  <dd>UniFi OS: local, cloud, or hybrid</dd>
                </div>
              </dl>
              <h4 className="unifi-page-snapshot-related">Related services</h4>
              <ul className="unifi-page-snapshot-links">
                <li>
                  <a href="/services/network-infrastructure">Network Infrastructure</a>
                </li>
                <li>
                  <a href="/services">All Services</a>
                </li>
              </ul>
            </aside>
          </div>
        </div>
      </section>

      <section id="lineup" className="unifi-page-section unifi-page-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Product Guide</h6>
            <h2 className="title-w3l">UniFi hardware we specify and install</h2>
            <p className="net-infra-section-intro mx-auto">
              Hardware refreshed {LINEUP_UPDATED}. The lineup below covers the components we deploy most
              often. Final selection depends on square footage, user density, camera or access-control
              needs, and whether you want an all-in-one gateway or a dedicated security appliance.
            </p>
          </div>
          <div className="unifi-page-product-grid unifi-page-product-grid-wide">
            {PRODUCTS.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      <section id="why-unifi" className="unifi-page-section">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Why UniFi</h6>
            <h2 className="title-w3l">Why UniFi is our default for new builds</h2>
            <p className="net-infra-section-intro mx-auto">
              UniFi is where we start on greenfield and refresh projects. It gives us a consistent
              design baseline and a management experience your team can actually maintain.
            </p>
          </div>
          <ul className="unifi-page-why-list">
            {WHY_UNIFI.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="installation" className="unifi-page-section unifi-page-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">What We Install</h6>
            <h2 className="title-w3l">From site survey to documented handoff</h2>
            <p className="net-infra-section-intro mx-auto">
              Installation scope follows the design. We coordinate structured cabling with your
              low-voltage contractors where needed and validate performance before sign-off.
            </p>
          </div>
          <ul className="unifi-page-services-list">
            {INSTALL_SERVICES.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="coverage" className="unifi-page-section">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Service Areas</h6>
            <h2 className="title-w3l">Westchester &amp; Fairfield County coverage</h2>
            <p className="net-infra-section-intro mx-auto">
              We install UniFi for residential estates, professional offices, and mixed-use properties
              across Westchester and Fairfield County, including White Plains, Scarsdale, Darien,
              Greenwich, and Stamford.
            </p>
          </div>
          <ul className="unifi-page-areas">
            {SERVICE_AREAS.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="faqs" className="unifi-page-section unifi-page-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">FAQs</h6>
            <h2 className="title-w3l">Questions about UniFi deployments</h2>
          </div>
          <div className="unifi-page-faq-grid">
            {FAQS.map((faq) => (
              <FaqItem key={faq.question} {...faq} />
            ))}
          </div>
        </div>
      </section>

      <section className="net-infra-cta">
        <div className="container text-center">
          <h4 className="net-infra-cta-title">Ready to scope a UniFi project?</h4>
          <p className="net-infra-cta-text">
            Send your floor plans and timeline. We will recommend hardware, outline the install scope,
            and explain what documentation you receive at handoff.
          </p>
          <a href="/contact" className="btn btn-style solace-btn-primary">
            Start a Conversation
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
